import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * Dynamic browser-tab titles registered by mounted screens (see
 * `usePageTitle`). Kept as a stack so nested registrations behave: the most
 * recently registered title wins, and unmounting restores the previous one
 * (or the route's static title when the stack empties).
 */

type Entry = { id: string; title: string };

type DocumentTitleState = {
  entries: Entry[];
};

type DocumentTitleActions = {
  /** Adds the entry or updates its title in place (keeps its stack position). */
  setTitle: (id: string, title: string) => void;
  clearTitle: (id: string) => void;
};

export const useDocumentTitleStore = create<DocumentTitleState & DocumentTitleActions>()(
  devtools(
    (set) => ({
      entries: [],

      setTitle: (id, title) =>
        set(
          (state) => {
            const exists = state.entries.some((e) => e.id === id);
            return {
              entries: exists
                ? state.entries.map((e) => (e.id === id ? { id, title } : e))
                : [...state.entries, { id, title }],
            };
          },
          false,
          "setTitle"
        ),

      clearTitle: (id) => set((state) => ({ entries: state.entries.filter((e) => e.id !== id) }), false, "clearTitle"),
    }),
    { name: "documentTitle" }
  )
);

export const selectDynamicTitle = (state: DocumentTitleState) => state.entries[state.entries.length - 1]?.title ?? null;
